/* VSS Smart Search 2.4.1. Catalog-backed discovery, native retrieval and a single ranking core. */
(() => {
  if (customElements.get('vss-smart-search') || !window.VSSSearchCore) return;
  const VSSIntent = window.VSSSearchCore;
  let settingsNode, settingsRules=[];
  function rules() {
    const node=document.querySelector('template[data-vss-search-settings]');
    if(node===settingsNode)return settingsRules;
    settingsNode=node;
    settingsRules=node?[...node.content.querySelectorAll('[data-vss-rule]')].flatMap(el=>{
      try {const r=JSON.parse(el.dataset.vssRule);return r.enabled?[r]:[];}catch{return [];}
    }):[];
    return settingsRules;
  }
  const intentFor = value => VSSIntent.parse(value,rules());
  const cache = new Map();
  const MAX_CACHE = 20;
  const TTL = 5 * 60 * 1000;
  let activeSearch = null;
  const visible = (el) => !!el.getClientRects().length && getComputedStyle(el).visibility !== 'hidden' && !el.closest('[hidden], [inert]');
  const focusables = (root) => [...root.querySelectorAll('a[href],button:not([disabled]),input:not([type="hidden"]):not([disabled]),[tabindex="0"]')].filter(visible);

  class VSSSmartSearch extends HTMLElement {
    connectedCallback() {
      if (this.events || !this.querySelector('form')) return;
      this.events = new AbortController();
      this.form = this.querySelector('form');
      this.input = this.form.querySelector('[name="q"]');
      const searchLocation=new URL(location.href);
      const readableQuery=searchLocation.searchParams.get('vss_query');
      if(searchLocation.pathname===new URL(this.form.action,location.origin).pathname) {
        const value=readableQuery||searchLocation.searchParams.get('q');
        if(value)this.input.value=value;
      }
      this.reset = this.form.querySelector('.js-search-reset');
      this.status = this.form.querySelector('[data-vss-status]');
      this.header = this.closest('store-header');
      this.mobileQuery = matchMedia('(max-width: 768px)');
      this.mobile = this.mobileQuery.matches;
      this.generation = 0;
      this.layer = this.querySelector('[data-vss-shell]').content.firstElementChild.cloneNode(true);
      document.body.append(this.layer);
      this.panel = this.layer.querySelector('.vss-search-panel');
      this.body = this.layer.querySelector('[data-vss-body]');
      this.heading = this.layer.querySelector('[data-vss-heading]');
      this.footer = this.layer.querySelector('.vss-search-footer');
      this.initialHTML = this.body.innerHTML;
      this.listen(this.form, 'focusin', (e) => {
        if (e.target === this.input && !this.suppressFocus) this.open();
      });
      this.listen(this.input, 'click', () => this.open());
      this.listen(this.input, 'input', () => { if (!this.composing) this.queryChanged(); });
      this.listen(this.input, 'compositionstart', () => { this.composing = true; this.invalidate(); });
      this.listen(this.input, 'compositionend', () => { this.composing = false; this.queryChanged(); });
      this.listen(this.reset, 'click', () => {
        this.input.value = '';
        this.queryChanged();
        this.input.focus({ preventScroll: true });
      });
      this.listen(this.form, 'submit', (e) => {
        if (!this.query()) { e.preventDefault(); this.input.focus(); return; }
        const intent=intentFor(this.query());
        if(intent.changed) {
          e.preventDefault();const destination=this.fullSearchURL();this.close(false);window.location.assign(destination);return;
        }
        this.close(false);
      });
      this.listen(this.form, 'change', (e) => {
        if (e.target.closest('custom-select') && e.detail) {
          const hidden = this.form.querySelector('[name="filter.p.product_type"]');
          if (hidden) hidden.value = e.detail.selectedValue || '';
          this.queryChanged();
        }
      });
      this.listen(this.layer, 'click', (e) => {
        if (e.target.closest('[data-vss-dismiss]')) this.close(true);
        if (e.target.closest('[data-vss-retry]')) this.request();
      });
      this.listen(document, 'keydown', (e) => this.keydown(e), { capture: true });
      this.listen(document, 'click', (e) => this.documentClick(e), { capture: true });
      this.listen(document, 'focusin', (e) => {
        if (!this.isOpen || this.suppressFocus || this.form.contains(e.target) || this.layer.contains(e.target)) return;
        if (this.mobile) this.input.focus({ preventScroll: true });
        else this.close(false);
      });
      // Native menu opens on mouseenter. Keep the path from input to results safe;
      // explicit clicks/keyboard focus still hand control back to navigation.
      this.listen(document, 'mouseenter', (e) => {
        if (this.isOpen && e.target instanceof Element && e.target.closest('main-menu')) e.stopImmediatePropagation();
      }, { capture: true });
      this.listen(window, 'resize', () => this.scheduleGeometry(), { passive: true });
      this.listen(window, 'scroll', () => this.scheduleGeometry(), { passive: true });
      this.listen(window, 'pagehide', () => this.close(false));
      if (window.visualViewport) {
        this.listen(visualViewport, 'resize', () => this.scheduleGeometry(), { passive: true });
        this.listen(visualViewport, 'scroll', () => this.scheduleGeometry(), { passive: true });
      }
      this.listen(this.mobileQuery, 'change', () => {
        this.mobile = this.mobileQuery.matches;
        if (this.isOpen) {
          this.setPresentation();
          this.scheduleGeometry();
          this.input.focus({ preventScroll: true });
        }
      });
      this.resizeObserver = new ResizeObserver(() => this.scheduleGeometry());
      this.resizeObserver.observe(this.header || this);
      this.setupVoice();
      this.reset.hidden = !this.query();
      this.input.classList.toggle('search__input--dirty', !!this.query());
      this.input.setAttribute('aria-expanded','false');
    }

    listen(target, type, handler, options = {}) {
      if (target) target.addEventListener(type, handler, { ...options, signal: this.events.signal });
    }
    query() { return this.input.value.trim(); }
    invalidate() {
      this.generation += 1;
      clearTimeout(this.debounceTimer);
      this.requestController?.abort();
    }
    queryChanged() {
      this.invalidate();
      this.reset.hidden = !this.query();
      this.input.classList.toggle('search__input--dirty', !!this.query());
      if (!this.isOpen) this.open(false);
      this.updateViewAll();
      if (!this.query()) { this.renderStart(); return; }
      this.renderLoading();
      this.debounceTimer = setTimeout(() => this.request(), 250);
    }

    open(load = true, opener = this.input) {
      if (this.isOpen) return;
      activeSearch?.close(false);
      this.closeMenus();
      this.opener = opener;
      this.isOpen = true;
      activeSearch = this;
      this.setAttribute('open', '');
      this.input.setAttribute('aria-expanded','true');
      this.layer.hidden = false;
      this.scrollPosition = { x: scrollX, y: scrollY };
      const gap = Math.max(0, innerWidth - document.documentElement.clientWidth);
      this.savedBodyPad = document.body.style.getPropertyValue('--vss-search-body-pad');
      this.savedScrollY = document.body.style.getPropertyValue('--vss-search-scroll-y');
      document.body.style.setProperty('--vss-search-body-pad', `${parseFloat(getComputedStyle(document.body).paddingRight) + gap}px`);
      document.body.style.setProperty('--vss-search-scroll-y', `-${this.scrollPosition.y}px`);
      document.documentElement.classList.add('vss-search-locked');
      document.body.classList.add('vss-search-locked');
      this.setPresentation();
      this.geometry();
      this.watchMenus();
      if (this.mobile) this.input.focus({ preventScroll: true });
      this.updateViewAll();
      if (!this.query()) this.renderStart();
      else if (load) this.request();
    }

    close(restoreFocus = true) {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.invalidate();
      this.recognition?.abort();
      this.menuObserver?.disconnect();
      this.removeAttribute('open');
      this.input.setAttribute('aria-expanded','false');
      this.restoreBackground();
      this.restoreNavigation();
      this.restoreForm();
      this.layer.hidden = true;
      this.body.removeAttribute('aria-busy');
      this.status.textContent = '';
      document.documentElement.classList.remove('vss-search-locked');
      document.body.classList.remove('vss-search-locked', 'vss-search-mobile');
      for (const [key, value] of [['--vss-search-body-pad', this.savedBodyPad], ['--vss-search-scroll-y', this.savedScrollY]]) {
        if (value) document.body.style.setProperty(key, value);
        else document.body.style.removeProperty(key);
      }
      if (this.wasFixed) { scrollTo(this.scrollPosition.x, this.scrollPosition.y); this.wasFixed = false; }
      if (activeSearch === this) activeSearch = null;
      const fallback = this.mobile ? [...(this.header?.querySelectorAll('.js-show-search,.js-show-search-left') || [])].find(visible) : this.input;
      const focusTarget = this.opener?.isConnected && visible(this.opener) ? this.opener : fallback;
      if (restoreFocus && focusTarget?.isConnected && visible(focusTarget)) {
        this.suppressFocus = true;
        focusTarget.focus({ preventScroll: true });
        this.suppressFocus = false;
      }
    }

    setPresentation() {
      this.suppressFocus = true;
      if (this.mobile) {
        if (!this.formMarker) {
          this.formMarker = document.createElement('div');
          this.formMarker.setAttribute('aria-hidden', 'true');
          this.formMarker.style.height = `${this.form.getBoundingClientRect().height}px`;
          this.form.before(this.formMarker);
          this.layer.querySelector('.vss-search-field-dock').append(this.form);
        }
        this.panel.setAttribute('role', 'dialog');
        this.panel.setAttribute('aria-modal', 'true');
        this.panel.setAttribute('aria-label', 'Search');
        document.body.classList.add('vss-search-mobile');
        this.wasFixed = true;
        this.hideBackground();
      } else {
        this.restoreBackground();
        this.restoreForm();
        this.panel.setAttribute('role', 'region');
        this.panel.removeAttribute('aria-modal');
        this.panel.setAttribute('aria-label', 'Search suggestions');
        document.body.classList.remove('vss-search-mobile');
        if (this.wasFixed) { scrollTo(this.scrollPosition.x, this.scrollPosition.y); this.wasFixed = false; }
      }
      if (!this.mobile) this.hideNavigation();
      else this.restoreNavigation();
      this.footer.hidden = !this.mobile || !this.query();
      this.suppressFocus = false;
    }

    hideNavigation() {
      if (this.navState) return;
      this.navState = new Map();
      this.header?.querySelectorAll('main-menu').forEach((menu) => {
        this.navState.set(menu, { inert: menu.inert, ariaHidden: menu.getAttribute('aria-hidden') });
        menu.inert = true;
        menu.setAttribute('aria-hidden', 'true');
      });
      this.header?.classList.add('vss-search-nav-covered');
    }
    restoreNavigation() {
      this.navState?.forEach((state, menu) => {
        menu.inert = state.inert;
        if (state.ariaHidden === null) menu.removeAttribute('aria-hidden');
        else menu.setAttribute('aria-hidden', state.ariaHidden);
      });
      this.navState = null;
      this.header?.classList.remove('vss-search-nav-covered');
    }

    restoreForm() {
      if (!this.formMarker) return;
      this.formMarker.replaceWith(this.form);
      this.formMarker = null;
    }
    hideBackground() {
      if (this.inertElements) return;
      this.inertElements = new Map();
      const hide = () => {
        for (const el of document.body.children) {
          if (el === this.layer || /^(SCRIPT|STYLE|LINK)$/.test(el.tagName) || this.inertElements.has(el)) continue;
          this.inertElements.set(el, el.inert);
          el.inert = true;
        }
      };
      hide();
      this.backgroundObserver = new MutationObserver(hide);
      this.backgroundObserver.observe(document.body, { childList: true });
    }
    restoreBackground() {
      this.backgroundObserver?.disconnect();
      this.inertElements?.forEach((wasInert, el) => { el.inert = wasInert; });
      this.inertElements = null;
    }
    closeMenus() {
      document.querySelectorAll('main-menu').forEach((menu) => {
        clearTimeout(menu.menuLinkTimeout);
        clearTimeout(menu.singleLinkTimeout);
        const disclosures = [...menu.querySelectorAll('.main-nav__item--dropdown.is-open')];
        if (menu.mainContent?.classList.contains('is-open') || disclosures.length || menu.overlayOpen) {
          menu.opener = null;
          if (menu.mainContent?.classList.contains('is-open')) menu.closeMainMenu?.(false);
          disclosures.forEach((item) => menu.close?.(item, false));
          if (menu.overlayOpen) menu.toggleOverlay?.(false);
          menu.childNavOpen = false;
          if (typeof window.removeTrapFocus === 'function') window.removeTrapFocus();
        }
      });
    }
    watchMenus() {
      this.menuObserver?.disconnect();
      this.menuObserver = new MutationObserver(() => {
        if (this.isOpen && document.querySelector('main-menu .main-nav__item--dropdown.is-open,main-menu .main-menu__content.is-open')) this.close(false);
      });
      document.querySelectorAll('main-menu').forEach((menu) => this.menuObserver.observe(menu, { attributes: true, attributeFilter: ['class'], subtree: true }));
    }
    documentClick(e) {
      const trigger = e.target.closest?.('.js-show-search,.js-show-search-left');
      if (trigger && this.header?.contains(trigger) && this.mobile) {
        e.preventDefault();
        e.stopImmediatePropagation();
        this.open(true, trigger);
        this.input.focus({ preventScroll: true });
        return;
      }
      if (this.isOpen && !this.form.contains(e.target) && !this.layer.contains(e.target)) this.close(false);
    }

    keydown(e) {
      if (!this.isOpen || e.isComposing || this.composing) return;
      if (e.key === 'Escape') {
        // Let an open product-type select consume its own Escape first.
        if (e.target.closest('custom-select')?.listboxOpen) return;
        e.preventDefault(); e.stopImmediatePropagation(); this.close(true); return;
      }
      const links = [...this.body.querySelectorAll('a[href],button')].filter(visible);
      if (e.key === 'ArrowDown' && e.target === this.input && links.length) {
        e.preventDefault(); links[0].focus(); return;
      }
      const index = links.indexOf(e.target);
      if (index >= 0 && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
        e.preventDefault();
        if (e.key === 'ArrowUp' && index === 0) this.input.focus({ preventScroll: true });
        else links[Math.max(0, Math.min(links.length - 1, index + (e.key === 'ArrowDown' ? 1 : -1)))].focus();
        return;
      }
      if (e.key === 'Tab' && this.mobile) {
        const items = focusables(this.panel);
        if (!items.length) { e.preventDefault(); this.panel.focus(); return; }
        if (e.shiftKey && (e.target === items[0] || !this.panel.contains(e.target))) { e.preventDefault(); items.at(-1).focus(); }
        else if (!e.shiftKey && (e.target === items.at(-1) || !this.panel.contains(e.target))) { e.preventDefault(); items[0].focus(); }
      } else if (e.key === 'Tab' && !this.mobile && !e.shiftKey && this.form.contains(e.target)) {
        const formItems = focusables(this.form);
        if (e.target === formItems.at(-1)) {
          const first = focusables(this.panel)[0];
          if (first) { e.preventDefault(); first.focus(); }
        }
      } else if (e.key === 'Tab' && !this.mobile && e.shiftKey && e.target === focusables(this.panel)[0]) {
        e.preventDefault(); focusables(this.form).at(-1)?.focus();
      }
    }

    fullSearchURL() {
      return VSSIntent.url(intentFor(this.query()),this.form.action,location.origin,this.form.querySelector('[name="filter.p.product_type"]')?.value).href;
    }
    updateViewAll() {
      this.layer.querySelectorAll('[data-vss-viewall]').forEach((a) => {
        a.href = this.fullSearchURL();
        a.hidden = !this.query();
      });
      this.footer.hidden = !this.mobile || !this.query();
    }
    renderStart() {
      this.body.innerHTML = this.initialHTML;
      this.body.removeAttribute('aria-busy');
      this.layer.dataset.mode = 'start';
      this.heading.textContent = 'Search';
      this.status.textContent = '';
      this.updateViewAll();
    }
    renderLoading() {
      this.layer.dataset.mode = 'start';
      this.heading.textContent = 'Searching…';
      const box = document.createElement('div');
      box.className = 'vss-search-loading';
      const spinner = document.createElement('span');
      spinner.className = 'vss-search-spinner';
      spinner.setAttribute('aria-hidden', 'true');
      box.append(spinner, document.createTextNode(this.dataset.loadingText || 'Loading…'));
      this.body.replaceChildren(box);
      this.body.setAttribute('aria-busy', 'true');
      this.status.textContent = 'Searching…';
    }
    renderError() {
      this.layer.dataset.mode = 'error';
      this.heading.textContent = 'Search';
      const box = document.createElement('div');
      box.className = 'vss-search-error';
      const title = document.createElement('h3'); title.textContent = 'Suggestions could not load';
      const copy = document.createElement('p'); copy.textContent = 'Try again or open the full search results.';
      const retry = document.createElement('button'); retry.type = 'button'; retry.className = 'vss-search-pill'; retry.dataset.vssRetry = ''; retry.textContent = 'Try again';
      box.append(title, copy, retry, this.helpLink());
      this.body.replaceChildren(box);
      this.body.removeAttribute('aria-busy');
      this.status.textContent = 'Search suggestions could not load. Try again or view all results.';
    }
    helpLink() {
      const a = document.createElement('a');
      a.className = 'vss-search-help'; a.href = this.dataset.helpUrl; a.textContent = 'Expert help →';
      return a;
    }
    links(items) {
      const ul=document.createElement('ul');ul.className='vss-search-links';
      for(const item of items) {
        const li=document.createElement('li'),a=document.createElement('a'),label=document.createElement('span'),arrow=document.createElement('span');
        a.dataset.vssResultLink='';
        a.href=item.url;
        label.textContent=item.label||item.title;arrow.textContent='→';arrow.setAttribute('aria-hidden','true');
        a.append(label,arrow);li.append(a);ul.append(li);
      }
      return ul;
    }
    response(html) {
      const doc=new DOMParser().parseFromString(html,'text/html');
      const result=doc.querySelector('[data-vss-results]');
      if(!result)throw new Error('Missing VSS search response');
      const candidates=result.querySelector('template[data-vss-candidates]')?.content;
      const read=(selector,key)=>[...(candidates?.querySelectorAll(selector)||[])].flatMap(el=>{
        try{return [JSON.parse(el.dataset[key])];}catch{return [];}
      });
      const cards=[...result.querySelectorAll('.vss-search-product')].map(card=>{
        let product;
        try{product=JSON.parse(card.querySelector('[data-vss-facts]').dataset.vssFacts);}
        catch{product={title:card.querySelector('.vss-search-product-title')?.textContent||'',handle:card.querySelector('a')?.getAttribute('href')||''};}
        return {card,product};
      });
      return {result,cards,collections:read('[data-vss-collection]','vssCollection'),suggestions:read('[data-vss-suggestion]','vssSuggestion')};
    }
    renderResponse(html,extraHTML=null,verificationFailed=false) {
      const response=typeof html==='string'?this.response(html):html,extra=extraHTML?this.response(extraHTML):null,intent=intentFor(this.query());
      const byHandle=new Map();
      for(const entry of [...response.cards,...(extra?.cards||[])]) {
        const key=entry.product.handle;
        if(!byHandle.has(key)||VSSIntent.score(entry.product,intent).exact)byHandle.set(key,entry);
      }
      const entries=[...byHandle.values()],ranked=VSSIntent.rank(entries.map(e=>e.product),intent);
      const exact=ranked.filter(r=>r.exact);
      const selected=(exact.length?exact:ranked).slice(0,5);
      const results=response.result,grid=results.querySelector('.vss-search-grid');
      const zone=grid.querySelector('.vss-search-products-zone'),list=zone.querySelector('.vss-search-products');
      list.replaceChildren();
      for(const item of selected) {
        const card=entries[item.index].card;
        card.querySelector('[data-vss-facts]')?.remove();
        if(exact.length)card.classList.add('vss-search-product--prominent');
        list.append(card);
      }
      results.querySelector('template[data-vss-candidates]')?.remove();
      let mode=exact.length===1?'exact':exact.length>1?'matches':'discovery';
      if(!selected.length) {
        const box=document.createElement('div');box.className='vss-search-empty';
        const h=document.createElement('h3');h.textContent='No product suggestions for “'+this.query()+'”';
        const p=document.createElement('p');p.textContent='Try a product name or full part number, or open all results.';
        box.append(h,p,this.helpLink());zone.append(box);list.remove();
        grid.classList.add('vss-search-grid--no-products');mode='empty';
      }
      if(!exact.length) {
        const aside=document.createElement('aside');aside.className='vss-search-discover';aside.setAttribute('aria-label','Search suggestions and refinements');
        const catalog=[...response.collections,...(extra?.collections||[]),...(intent.contextRule?.includeCollections===false?[]:intent.contextRule?.collections||[])];
        const suggestions=VSSIntent.suggestions(intent,entries.map(e=>e.product),response.suggestions,rules(),catalog,{origin:location.origin,base:this.form.action,type:this.form.querySelector('[name="filter.p.product_type"]')?.value});
        for(const [group,title] of [['search','Suggested searches'],['refine','Refine your search']]) {
          const items=suggestions.filter(s=>s.group===group);if(!items.length)continue;
          const h=document.createElement('h3');h.textContent=title;
          aside.append(h,this.links(items));
        }
        if(suggestions.length)aside.dataset.vssSuggestionCount=String(suggestions.length);
        else aside.append(this.helpLink());
        grid.prepend(aside);grid.classList.add('vss-search-grid--discover');
        // Merchant collections are candidates, never forced overrides of more specific matches.
        const usedCollections=new Set(suggestions.filter(s=>s.collection).map(s=>s.collection));
        const related=VSSIntent.collections(catalog.filter(c=>!usedCollections.has(c.handle)),intent,location.origin);
        if(related.length) {
          const context=document.createElement('aside');context.className='vss-search-context';context.setAttribute('aria-label','Related collections');
          const heading=document.createElement('h3');heading.textContent='Related collections';
          context.append(heading,this.links(related));grid.append(context);grid.classList.add('vss-search-grid--context');
        }
      } else {
        list.classList.add('vss-search-exact-list');
        results.replaceChildren(list);
      }
      this.body.replaceChildren(results);
      this.layer.dataset.mode=mode;
      this.heading.textContent=exact.length?'Exact identifier '+(exact.length===1?'match':'matches'):'Products';
      this.body.removeAttribute('aria-busy');this.body.scrollTop=0;
      this.status.textContent=`${selected.length} product suggestions for ${this.query()}. View all results for more.`+
        (verificationFailed?' Additional part-number verification could not load.':'');
      this.updateViewAll();
    }
    async request() {
      if(!this.isOpen||!this.query()||this.composing)return;
      this.invalidate();
      const version=this.generation,controller=new AbortController();this.requestController=controller;
      const intent=intentFor(this.query()),type=this.form.querySelector('[name="filter.p.product_type"]')?.value;
      // Facets belong on native search, before pagination. Predictive has no facet parameter.
      const nativeFacet=!!type||intent.priceMin!==null||intent.priceMax!==null;
      const url=nativeFacet?VSSIntent.url(intent,this.form.action,location.origin,type):new URL(this.dataset.endpoint||`${window.Shopify?.routes?.root||'/'}search/suggest`,location.origin);
      if(nativeFacet){url.searchParams.set('view','vss-results');url.searchParams.set('type','product');}
      else {
        url.searchParams.set('q',intent.query);
        url.searchParams.set('resources[type]','product,query,collection');
        url.searchParams.set('resources[limit]','10');url.searchParams.set('resources[limit_scope]','each');
        url.searchParams.set('section_id','vss-smart-search');
      }
      const key=`${url.href}|${document.documentElement.lang}|${this.dataset.currency||window.Shopify?.currency?.active||''}`;
      const cached=cache.get(key);
      if(cached&&Date.now()-cached.at<TTL) {
        cache.delete(key);cache.set(key,cached);
        try{this.renderResponse(cached.html,cached.extraHTML);return;}catch{cache.delete(key);}
      }
      if(cached)cache.delete(key);
      this.renderLoading();
      let timedOut=false;
      const timeout=setTimeout(()=>{timedOut=true;controller.abort();},8000);
      const current=()=>version===this.generation&&this.isOpen&&this.isConnected;
      const fetchHTML=async endpoint=>{
        const r=await fetch(endpoint,{signal:controller.signal,headers:{Accept:'text/html'},credentials:'same-origin'});
        if(!r.ok)throw new Error(`Search HTTP ${r.status}`);
        return r.text();
      };
      try {
        const html=await fetchHTML(url);if(!current())return;
        const primary=this.response(html);
        this.renderResponse(primary); // Do not hold usable results while verifying an identifier.
        let extraHTML=null,verificationFailed=false;
        if(intent.code&&!primary.cards.some(e=>VSSIntent.score(e.product,intent).exact)) {
          const exactURL=VSSIntent.url(intent,this.form.action,location.origin,type);
          exactURL.searchParams.set('q',VSSIntent.exactQuery(intent.identifierQuery));
          exactURL.searchParams.set('type','product');exactURL.searchParams.set('view','vss-results');
          try {extraHTML=await fetchHTML(exactURL);if(current())this.renderResponse(html,extraHTML);}
          catch(error){verificationFailed=true;if(current())this.renderResponse(html,null,true);}
        }
        if(!current())return;
        if(!verificationFailed) {
          cache.delete(key);cache.set(key,{at:Date.now(),html,extraHTML});
          while(cache.size>MAX_CACHE)cache.delete(cache.keys().next().value);
        }
      } catch(error) {
        controller.abort();
        if(current()&&(error.name!=='AbortError'||timedOut))this.renderError();
      } finally {clearTimeout(timeout);}
    }

    scheduleGeometry() {
      if (!this.isOpen || this.geometryFrame) return;
      this.geometryFrame = requestAnimationFrame(() => { this.geometryFrame = null; this.geometry(); });
    }
    geometry() {
      if (!this.isOpen) return;
      const viewport = window.visualViewport;
      const height = viewport?.height || innerHeight;
      this.layer.style.setProperty('--vss-search-vv-height', `${height}px`);
      this.layer.style.setProperty('--vss-search-vv-top', `${viewport?.offsetTop || 0}px`);
      this.layer.dataset.keyboard = String(this.mobile && innerHeight - height > 140);
      if (!this.mobile) {
        const rect = (this.header || this.form).getBoundingClientRect();
        const left = this.header ? Math.max(0, rect.left) : 0;
        const width = this.header ? Math.min(document.documentElement.clientWidth - left, rect.width) : document.documentElement.clientWidth;
        // The compact header has two grid rows. Cover navigation (row 2),
        // while keeping the logo/search/actions (row 1) visible and operable.
        const menu = [...(this.header?.querySelectorAll('main-menu') || [])]
          .find((el) => el.getBoundingClientRect().height > 0);
        const menuRect = menu?.getBoundingClientRect();
        const formRect = this.form.getBoundingClientRect();
        const edge = menuRect && menuRect.top >= formRect.bottom - 2 ? menuRect.top : rect.bottom;
        const top = Math.max(0, Math.min(edge, innerHeight - 120));
        this.layer.style.setProperty('--vss-search-top', `${top}px`);
        this.layer.style.setProperty('--vss-search-left', `${left}px`);
        this.layer.style.setProperty('--vss-search-width', `${width}px`);
        this.layer.style.setProperty('--vss-search-height', `${Math.max(80, innerHeight - top - 12)}px`);
      }
    }
    setupVoice() {
      const button = this.form.querySelector('[data-vss-voice]');
      const Recognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (!button || !Recognition) return;
      button.hidden = false;
      this.recognition = new Recognition();
      this.recognition.lang = document.documentElement.lang || 'en-US';
      this.recognition.interimResults = false;
      this.recognition.onresult = (e) => {
        if (!this.isOpen) return;
        this.input.value = e.results[0][0].transcript;
        this.queryChanged();
        this.input.focus({ preventScroll: true });
      };
      this.recognition.onerror = (e) => { if (this.isOpen && e.error !== 'aborted') this.status.textContent = 'Voice search is unavailable. Please type your search.'; };
      this.recognition.onend = () => button.removeAttribute('aria-pressed');
      this.listen(button, 'click', () => {
        this.open(false);
        try { this.recognition.start(); button.setAttribute('aria-pressed', 'true'); this.status.textContent = 'Listening…'; }
        catch { this.recognition.stop(); }
      });
    }
    disconnectedCallback() {
      if (!this.events) return;
      this.close(false);
      this.invalidate();
      this.events.abort(); this.events = null;
      this.resizeObserver?.disconnect(); this.menuObserver?.disconnect();
      cancelAnimationFrame(this.geometryFrame); this.geometryFrame = null;
      this.recognition?.abort();
      this.layer?.remove();
    }
  }
  customElements.define('vss-smart-search', VSSSmartSearch);
  document.addEventListener('shopify:section:load',()=>{cache.clear();if(activeSearch?.isOpen)activeSearch.queryChanged();});

  const restoreReadableSearch=()=>{
    const current=new URL(location.href),raw=current.searchParams.get('vss_query');
    const readable=raw||current.searchParams.get('q');
    document.querySelectorAll('[data-vss-readable-query]').forEach(el=>{el.textContent=readable?'Results for “'+readable+'”':'';el.hidden=!readable;});
    if(!raw)return;
    document.querySelectorAll('form').forEach(form=>{
      if(!form.querySelector('input[type="hidden"][name="q"]')||form.querySelector('[name="vss_query"]'))return;
      const input=document.createElement('input');input.type='hidden';input.name='vss_query';input.value=raw;form.append(input);
    });
    document.querySelectorAll('[data-vss-query-message]').forEach(el=>{
      const technical=el.dataset.vssQueryMessage;
      if(technical)el.textContent=el.textContent.replace(technical,raw);
    });
    document.querySelectorAll('facet-filters a[href]').forEach(a=>{
      const href=a.getAttribute('href');if(!href||href.startsWith('#'))return;
      const u=new URL(href,location.href);
      if(u.origin===current.origin&&u.pathname===current.pathname&&u.searchParams.get('q')===current.searchParams.get('q')) {
        u.searchParams.set('vss_query',raw);a.href=u.href;
      }
    });
  };
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',restoreReadableSearch,{once:true});
  else restoreReadableSearch();
  document.addEventListener('on:facet-filters:updated',restoreReadableSearch);
  window.addEventListener('popstate',restoreReadableSearch);
})();
